import { Routes } from '@angular/router';
import { AppLayout } from './core/layout/component/app.layout';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { ReinitialiserComponent } from './pages/auth/reinitialiser/reinitialiser.component';
import { SigninComponent } from './pages/auth/signin/signin.component';
import { ValidateComponent } from './pages/auth/validate/validate.component';
import { QuotesPreviewComponent } from './pages/quotes/quotes-preview/quotes-preview.component';
import { InvoicesPreviewComponent } from './pages/invoices/invoices-preview/invoices-preview.component';
import { AdminGuard, rootRedirectGuard } from './core/services/guard.service';
import { SoloPlanGuard } from './solo/guards/solo-plan.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { launcherGuard } from './core/guards/launcher.guard';
import { moduleLockedGuard } from './core/guards/module-locked.guard';
import { PERM } from './core/constants/app-permissions';
import { WelcomeWizardComponent } from './core/components/welcome-wizard/welcome-wizard.component';
import { formDetailRoute } from './cms/features/forms-manager/form-detail/form-detail.route';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [rootRedirectGuard],
    component: SigninComponent
  },
  {
    path: 'login',
    component: SigninComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'bienvenue',
    component: WelcomeWizardComponent,
    canActivate: [AdminGuard],
    data: { renderMode: 'server' }
  },
  {
    path: 'acces-refuse',
    canActivate: [AdminGuard],
    loadComponent: () => import('./pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent),
    data: { renderMode: 'server' }
  },
  {
    // Espace fermé par TRP (abonnement terminé) : servi par AccesCoupeInterceptor.
    path: 'espace-ferme',
    loadComponent: () => import('./pages/espace-ferme/espace-ferme.component').then(m => m.EspaceFermeComponent),
    data: { renderMode: 'server' }
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'reinitialiser/:token',
    component: ReinitialiserComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'validate/:token',
    component: ValidateComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'join/:token',
    loadComponent: () => import('./pages/join/join.component').then(m => m.JoinComponent),
    data: { renderMode: 'server' }
  },
  {
    path: 'devis/:uuid',
    component: QuotesPreviewComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'factures/:uuid',
    component: InvoicesPreviewComponent,
    data: {
      renderMode: 'server'
    }
  },
  {
    path: 'custom',
    canActivate: [AdminGuard],
    loadChildren: () => import('./custom/route').then(m => m.CUSTOM_ROUTES)
  },
  {
    path: 'moni',
    component: AppLayout,
    canActivate: [AdminGuard, permissionGuard],
    data: { permissions: [PERM.accessMoni], permissionMode: 'any' },
    loadChildren: () => import('./pages/route').then(m => m.PAGES_ROUTES)
  },
  {
    path: 'pos',
    canActivate: [AdminGuard, permissionGuard, moduleLockedGuard],
    data: { permissions: [PERM.createOrders, PERM.viewOrders], permissionMode: 'any', module: 'polisi' },
    loadChildren: () => import('./pages/pos/pos.routes').then(m => m.POS_ROUTES)
  },
  {
    path: 'skedi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'skedi' },
    loadChildren: () => import('./skedi/skedi.routes').then(m => m.SKEDI_ROUTES)
  },
  {
    path: 'kratisi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'kratisi' },
    loadChildren: () => import('./kratisi/kratisi.routes').then(m => m.KRATISI_ROUTES)
  },
  {
    path: 'logistiki',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'logistiki' },
    loadChildren: () => import('./logistiki/logistiki.routes').then(m => m.LOGISTIKI_ROUTES)
  },
  {
    path: 'pinaki',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'pinaki' },
    loadChildren: () => import('./pinaki/pinaki.routes').then(m => m.PINAKI_ROUTES)
  },
  {
    path: 'entoli',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'entoli' },
    loadChildren: () => import('./entoli/entoli.routes').then(m => m.ENTOLI_ROUTES),
  },
  {
    path: 'agelii',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'agelii' },
    loadChildren: () => import('./agelii/agelii.routes').then(m => m.AGELII_ROUTES),
  },
  {
    path: 'arogi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'arogi' },
    loadChildren: () => import('./arogi/arogi.routes').then(m => m.AROGI_ROUTES),
  },
  {
    path: 'odigi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'odigi' },
    loadChildren: () => import('./odigi/odigi.routes').then(m => m.ODIGI_ROUTES),
  },
  {
    path: 'kleidi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'kleidi' },
    loadChildren: () => import('./kleidi/kleidi.routes').then(m => m.KLEIDI_ROUTES),
  },
  {
    path: 'pyxidi',
    canActivate: [AdminGuard, moduleLockedGuard],
    data: { module: 'pyxidi' },
    loadChildren: () => import('./pyxidi/pyxidi.routes').then(m => m.PYXIDI_ROUTES),
  },

  // ─── CMS / Site builder ───────────────────────────────────────────────────
  {
    path: 'cms',
    canActivate: [AdminGuard, permissionGuard, moduleLockedGuard],
    data: { permissions: [PERM.accessMoni], permissionMode: 'any', module: 'eezili' },
    loadComponent: () => import('./cms/features/admin-shell/admin-shell').then(m => m.AdminShell),
    children: [
      { path: '', redirectTo: 'pages', pathMatch: 'full' },
      { path: 'pages',      loadComponent: () => import('./cms/features/pages/pages-list/pages-list').then(m => m.PagesList) },
      { path: 'pages/new',  loadComponent: () => import('./cms/features/pages/page-form/page-form').then(m => m.PageForm) },
      { path: 'pages/:uuid', loadComponent: () => import('./cms/features/pages/page-form/page-form').then(m => m.PageForm) },
      { path: 'menus',       loadComponent: () => import('./cms/features/menus/menus-list/menus-list').then(m => m.MenusList) },
      { path: 'menus/:uuid', loadComponent: () => import('./cms/features/menus/menu-editor/menu-editor').then(m => m.MenuEditor) },
      { path: 'posts',      loadComponent: () => import('./cms/features/posts/posts-list/posts-list').then(m => m.PostsList) },
      { path: 'posts/new',  loadComponent: () => import('./cms/features/posts/post-form/post-form').then(m => m.PostForm) },
      { path: 'posts/:uuid', loadComponent: () => import('./cms/features/posts/post-form/post-form').then(m => m.PostForm) },
      { path: 'post-categories',      loadComponent: () => import('./cms/features/post-categories/post-categories-list/post-categories-list').then(m => m.PostCategoriesList) },
      { path: 'post-categories/new',  loadComponent: () => import('./cms/features/post-categories/post-category-form/post-category-form').then(m => m.PostCategoryForm) },
      { path: 'post-categories/:uuid', loadComponent: () => import('./cms/features/post-categories/post-category-form/post-category-form').then(m => m.PostCategoryForm) },
      { path: 'catalog',    loadComponent: () => import('./cms/features/catalog/catalog').then(m => m.Catalog) },
      { path: 'forms',      loadComponent: () => import('./cms/features/forms-manager/forms-list/forms-list').then(m => m.FormsList) },
      formDetailRoute,
      { path: 'site',       loadComponent: () => import('./cms/features/site-settings/site-settings').then(m => m.SiteSettings) },
    ]
  },
  {
    path: 'cms/pages/:pageUuid/builder',
    canActivate: [AdminGuard, permissionGuard, moduleLockedGuard],
    data: { permissions: [PERM.accessMoni], permissionMode: 'any', module: 'eezili' },
    loadComponent: () => import('./cms/features/builder-editor/pages/builder-editor-page/builder-editor-page').then(m => m.BuilderEditorPage),
  },
  {
    path: 'cms/posts/:postUuid/builder',
    canActivate: [AdminGuard, permissionGuard, moduleLockedGuard],
    data: { permissions: [PERM.accessMoni], permissionMode: 'any', module: 'eezili' },
    loadComponent: () => import('./cms/features/builder-editor/pages/builder-editor-page/builder-editor-page').then(m => m.BuilderEditorPage),
  },

  {
    path: 'solo',
    canActivate: [AdminGuard, SoloPlanGuard],
    loadChildren: () => import('./solo/solo.routes').then(m => m.SOLO_ROUTES),
  },

  {
    path: 'kiosk',
    loadComponent: () =>
      import('./features/kiosk/kiosk.component').then(m => m.KioskComponent),
    // Pas de guard — le token kiosk est l'authentification
    data: { renderMode: 'server' }
  },

  {
    path: 'launcher',
    canActivate: [AdminGuard, launcherGuard],
    loadComponent: () => import('./core/components/app-launcher/app-launcher.component').then(m => m.AppLauncherComponent),
    data: { renderMode: 'server' }
  },

  {
    // Centre d'aide (tutos servis par TRP via l'API du caser), dans le gabarit de MONI.
    path: 'aide',
    component: AppLayout,
    canActivate: [AdminGuard],
    loadChildren: () => import('./aide/aide.routes').then(m => m.AIDE_ROUTES)
  },

  {
    path: '**',
    loadComponent: () => import('./cms/features/fo-page/fo-page').then(m => m.FoPage),
  }
];
